import React, { useEffect, useState } from "react";
import axiosInstance from "../../axiosInstance";
import { useAuth } from "../../contexts/AuthContext";
import Pagination from "../../components/Pagination/Pagination";
import GeneralTable from "../../components/Table/GeneralTable";
import StatusCard from "../../components/StatusCard/StatusCard";
import "./TeamLeaveReport.scss";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";

const TeamLeaveReport = ({ onClose, managerId }) => {
  const { user } = useAuth();

  const [leaveData, setLeaveData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [dataPerPage] = useState(10);

  const [selectedRole, setSelectedRole] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");

  const [roles, setRoles] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Role toggle states
  const [selectedViewRole, setSelectedViewRole] = useState("");
  const [availableRoles, setAvailableRoles] = useState([]);

  // Role detection useEffect
  useEffect(() => {
    if (!user) return;

    const roles = [];
    if (user.authorities?.includes("ROLE_ADMIN")) roles.push("ADMIN");
    if (user.authorities?.includes("ROLE_HR")) roles.push("HR");
    if (user.authorities?.includes("ROLE_MANAGER")) roles.push("MANAGER");

    setAvailableRoles(roles);

    // Set default role (prioritize ADMIN > HR > MANAGER)
    if (roles.includes("ADMIN")) {
      setSelectedViewRole("ADMIN");
    } else if (roles.includes("HR")) {
      setSelectedViewRole("HR");
    } else if (roles.includes("MANAGER")) {
      setSelectedViewRole("MANAGER");
    }
  }, [user]);

  const fetchLeaveData = async (roleOverride = null) => {
    const roleToUse = roleOverride || selectedViewRole;

    if (!roleToUse) return;

    setLoading(true);
    try {
      let endpoint;

      // Determine endpoint based on selected role
      switch (roleToUse) {
        case "ADMIN":
          endpoint = `${process.env.REACT_APP_API_BASE_URL}/leave-history/Report`;
          break;
        case "HR":
          endpoint = `${process.env.REACT_APP_API_HR_URL}/leave-history/Report`;
          break;
        case "MANAGER":
          endpoint = `${process.env.REACT_APP_API_MANAGER_URL}/leave-history/Report`;
          break;
        default:
          endpoint = `${process.env.REACT_APP_API_MANAGER_URL}/leave-history/Report`;
      }

      const response = await axiosInstance.get(endpoint);

      if (response.status !== 200) {
        throw new Error("Failed to fetch leave data");
      }

      const data = response.data.data;

      // Sort data by date (latest first)
      const sortedData = [...data].sort((a, b) => {
        const dateA =
          a.dates && a.dates.length > 0 ? new Date(a.dates[0]) : new Date(0);
        const dateB =
          b.dates && b.dates.length > 0 ? new Date(b.dates[0]) : new Date(0);
        return dateB - dateA; // Latest first
      });

      setLeaveData(sortedData);
      setFilteredData(sortedData);

      // Extract unique values for filters - Fixed department parsing
      const extractedRoles = [
        ...new Set(sortedData.map((leave) => leave.role).filter(Boolean)),
      ];
      const extractedStatuses = [
        ...new Set(
          sortedData.map((leave) => leave.leaveStatus).filter(Boolean),
        ),
      ];
      // Department is already a string, no need to parse JSON
      const extractedDepartments = [
        ...new Set(sortedData.map((leave) => leave.department).filter(Boolean)),
      ];

      setRoles(extractedRoles);
      setStatuses(extractedStatuses);
      setDepartments(extractedDepartments);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  // Role switching handler
  const handleRoleSwitch = async (role) => {
    if (role === selectedViewRole) return;

    setSelectedViewRole(role);

    // Reset filters when switching roles
    setSelectedRole("");
    setSelectedStatus("");
    setSelectedDepartment("");

    // Fetch new data for the selected role
    await fetchLeaveData(role);
  };

  useEffect(() => {
    if (!selectedViewRole) return; // Don't fetch until role is determined

    fetchLeaveData();
  }, [managerId, selectedViewRole]);

  // Filter the data based on selected filters
  useEffect(() => {
    let filtered = leaveData;

    if (selectedRole) {
      filtered = filtered.filter((leave) => leave.role === selectedRole);
    }
    if (selectedStatus) {
      filtered = filtered.filter(
        (leave) => leave.leaveStatus === selectedStatus,
      );
    }
    if (selectedDepartment) {
      // Department is a string, not JSON - Fixed filtering
      filtered = filtered.filter(
        (leave) => leave.department === selectedDepartment,
      );
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [selectedRole, selectedStatus, selectedDepartment, leaveData]);

  // Calculate the total pages based on filtered data
  const totalPages = Math.ceil(filteredData.length / dataPerPage);

  // Paginate the filtered data
  const indexOfLastData = currentPage * dataPerPage;
  const indexOfFirstData = indexOfLastData - dataPerPage;
  const currentData = filteredData.slice(indexOfFirstData, indexOfLastData);

  // Handle page change
  const onPageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Format department: show first 2 and "+X more"
  const formatDepartment = (departmentString) => {
    if (!departmentString) return "—";
    const departments = departmentString
      .split(",")
      .map((d) => d.trim())
      .filter((d) => d);

    if (departments.length === 0) return "—";
    if (departments.length <= 2) return departments.join(", ");

    return (
      <span title={departments.join(", ")}>
        {departments[0]}, {departments[1]} +{departments.length - 2} more
      </span>
    );
  };

  // Format date range: "17th - 19th July 2025"
  const formatDateRangeWithTooltip = (dates) => {
    if (!dates || dates.length === 0) return "—";

    const sortedDates = [...dates].sort((a, b) => new Date(a) - new Date(b));
    const start = new Date(sortedDates[0]);
    const end = new Date(sortedDates[sortedDates.length - 1]);

    const addSuffix = (day) => {
      if (day > 3 && day < 21) return `${day}th`;
      switch (day % 10) {
        case 1:
          return `${day}st`;
        case 2:
          return `${day}nd`;
        case 3:
          return `${day}rd`;
        default:
          return `${day}th`;
      }
    };

    const range =
      start.toDateString() === end.toDateString()
        ? `${addSuffix(start.getDate())} ${start.toLocaleString("default", {
            month: "short",
          })} ${start.getFullYear()}`
        : `${addSuffix(start.getDate())} - ${addSuffix(
            end.getDate(),
          )} ${end.toLocaleString("default", {
            month: "long",
          })} ${end.getFullYear()}`;

    return <span title={sortedDates.join(", ")}>{range}</span>;
  };

  // Helper function to format fallback status with correct mapping
  const formatFallbackStatus = (status) => {
    if (!status) return "—";

    // Map API status values to display values
    const statusMap = {
      REQUESTED: "Yet to Accept",
      APPROVED: "Accepted",
      REJECTED: "Rejected",
      // Legacy support (in case old values still exist)
      ACCEPTED: "Accepted",
      PENDING: "Yet to Accept",
      YET_TO_ACCEPT: "Yet to Accept",
    };

    const normalizedStatus = status.toUpperCase();
    return statusMap[normalizedStatus] || status;
  };

  // Helper function to render fallback status with badge styling
  const renderFallbackStatusBadge = (status) => {
    if (!status) return <span className="fallback-status-badge empty">—</span>;

    const normalizedStatus = status.toUpperCase();

    // Determine badge class based on status
    let badgeClass = "fallback-status-badge";

    if (normalizedStatus === "REQUESTED") {
      badgeClass += " pending";
    } else if (
      normalizedStatus === "APPROVED" ||
      normalizedStatus === "ACCEPTED"
    ) {
      badgeClass += " accepted";
    } else if (normalizedStatus === "REJECTED") {
      badgeClass += " rejected";
    }

    return <span className={badgeClass}>{formatFallbackStatus(status)}</span>;
  };

  // Define columns for GeneralTable - Leave Status moved to last
  const columns = [
    { header: "Employee", accessor: "employeeName" },
    { header: "Role", accessor: "role" },
    { header: "Department", accessor: "department" },
    { header: "Leave Type", accessor: "leaveType" },
    { header: "Reason", accessor: "reason" },
    { header: "Leave Period", accessor: "leavePeriod" },
    { header: "Fallback Employee", accessor: "fallbackEmployeeName" },
    { header: "Fallback Status", accessor: "leaveFallbackStatus" },
    { header: "Responsibility", accessor: "responsibility" },
    { header: "Leave Status", accessor: "leaveStatus" }, // Moved to last
  ];

  const formatReason = (reason) => {
    if (!reason) return "—";
    if (reason.length <= 40) return reason;
    return <span title={reason}>{reason.slice(0, 40)}...</span>;
  };

  // Transform data for GeneralTable
  const tableData = currentData.map((leave) => ({
    employeeName: leave.employeeName || "—",
    role: leave.role || "—",
    department: formatDepartment(leave.department),
    leaveType: leave.leaveType || "—",
    reason: formatReason(leave.reason),
    leavePeriod: formatDateRangeWithTooltip(leave.dates),
    fallbackEmployeeName: leave.fallbackEmployeeName || "—",
    leaveFallbackStatus: renderFallbackStatusBadge(leave.leaveFallbackStatus),
    responsibility: leave.responsibiltiy || "—",
    leaveStatus: <StatusCard status={leave.leaveStatus} />,
  }));

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="report-container">
      {/* <div className="filters">
        <div className="filters">
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            className="filter-select"
          >
            <option value="">All Roles</option>
            {roles.map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>

          <select
            value={selectedDepartment}
            onChange={(e) => setSelectedDepartment(e.target.value)}
            className="filter-select"
          >
            <option value="">All Departments</option>
            {departments.map((department) => (
              <option key={department} value={department}>
                {department}
              </option>
            ))}
          </select>
        </div>

        <div className="status-buttons">
          {statuses.map((status) => (
            <button
              key={status}
              onClick={() => setSelectedStatus(status)}
              className={`status-button ${
                selectedStatus === status ? "active" : ""
              }`}
            >
              {status}
            </button>
          ))}
          <button
            onClick={() => setSelectedStatus("")}
            className={`status-button ${selectedStatus === "" ? "active" : ""}`}
          >
            All Statuses
          </button>
        </div>
      </div> */}
      {/* GeneralTable Component */}
      <GeneralTable
        columns={columns}
        data={tableData}
        title="Team Leave Report"
        titleActions={
          availableRoles.length > 1 && (
            <div className="team-page-toolbar-actions">
              {availableRoles.map((role) => (
                <button
                  key={role}
                  className={`role-toggle-btn ${selectedViewRole === role ? "active" : ""}`}
                  onClick={() => handleRoleSwitch(role)}
                  disabled={loading}
                >
                  {role === "ADMIN" ? "Admin" : role === "HR" ? "HR" : "Manager"}
                </button>
              ))}
            </div>
          )
        }
      />
      {/* Pagination Component */}
      <div className="pagination">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
          className="pagination-button"
        />
      </div>
    </div>
  );
};

export default TeamLeaveReport;
